import {
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import DotsIcon from '@/assets/icons/dots.svg?react';
import SortIcon from '@/assets/icons/sort.svg?react';
import { TDate } from '@/components/i18n/date.i18n.component';
import { T } from '@/components/i18n/message.i18n.component';
import { TTime } from '@/components/i18n/time.i18n.component';
import Button from '@/components/ui/button/button.component';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown/dropdown.component';
import { Pill } from '@/components/ui/pill/pill.component';
import { LifecycleStatus, Product } from '@/gql/graphql';
import { useUser } from '@/hooks/use-user/use-user.hook';
import {
  isProductCopyEnabled,
  isProductDeleteEnabled,
  isProductEditEnabled,
  isProductStatusChangeEnabled,
} from '@/lib/product.utils';
import { cn } from '@/lib/utils';
import { productTableMessages } from './product-table.messages';

const columnHelper = createColumnHelper<Product>();

const tableConfig = (t: IntlShape) => [
  columnHelper.accessor((row) => row.name, {
    id: 'name',
    header: () => (
      <span data-testid="product-table-header-name">
        <T {...productTableMessages.nameHeader} />
      </span>
    ),
    cell: (info) => <span data-testid={`product-table-cell-${info.row.original.id}-name`}>{info.getValue()}</span>,
  }),
  columnHelper.accessor((row) => row.description, {
    id: 'description',
    header: () => (
      <span data-testid="product-table-header-description">
        <T {...productTableMessages.descriptionHeader} />
      </span>
    ),
    cell: (info) => (
      <span data-testid={`product-table-cell-${info.row.original.id}-description`}>{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor((row) => row.version, {
    id: 'version',
    header: () => (
      <span data-testid="product-table-header-version">
        <T {...productTableMessages.versionHeader} />
      </span>
    ),
    cell: (info) => <span data-testid={`product-table-cell-${info.row.original.id}-version`}>{info.getValue()}</span>,
  }),
  columnHelper.accessor((row) => row.lifecycleStatus, {
    id: 'status',
    header: () => (
      <span data-testid="product-table-header-status">
        <T {...productTableMessages.statusHeader} />
      </span>
    ),
    cell: (info) => (
      <Pill
        data-testid={`product-table-cell-${info.row.original.id}-status`}
        productStatus={info.getValue()}
        text={info.getValue()}
      />
    ),
  }),

  columnHelper.accessor((row) => row.updatedAt, {
    id: 'updatedOn',
    header: () => (
      <span data-testid="product-table-header-updatedAt">
        <T {...productTableMessages.updatedOnHeader} />
      </span>
    ),
    cell: (info) => (
      <span data-testid={`product-table-cell-${info.row.original.id}-updatedAt`} className="text-secondary-text">
        {t.formatMessage(productTableMessages.updatedOn, {
          date: <TDate value={new Date(info.getValue())} />,
          time: <TTime value={new Date(info.getValue())} />,
        })}
      </span>
    ),
  }),
];

type Props = {
  data: Product[];
  onRowClick?: (id: string) => void;
  handleEdit: (product: Product) => void;
  handleUseAsNewTemplate: (product: Product) => void;
  handleDelete: (product: Product) => void;
  handleStatusChange: (product: Product) => void;
};

export const ProductTable = ({
  data,
  onRowClick,
  handleEdit,
  handleUseAsNewTemplate,
  handleDelete,
  handleStatusChange,
}: Props) => {
  const t = useIntl();
  const [sorting, setSorting] = useState<SortingState>([]);
  const { guid } = useUser();

  const table = useReactTable({
    data,
    state: {
      sorting,
    },
    columns: tableConfig(t),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <table data-testid="product-table" className="border-separate border-spacing-y-2 w-full">
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header, index) => (
              <th
                scope="col"
                className={cn(
                  'typo-secondary text-left text-secondary-text px-2 pb-2',
                  index === 0 && 'pl-4 min-w-[150px]',
                  index === 1 && 'min-w-[200px]',
                  index === headerGroup.headers.length - 1 && 'text-right',
                  header.column.getCanSort() && 'cursor-pointer',
                )}
                // eslint-disable-next-line react/jsx-handler-names
                onClick={header.column.getToggleSortingHandler()}
                key={header.id}
              >
                <span
                  className={cn(
                    'flex flex-row items-center gap-x-1',
                    index === headerGroup.headers.length - 1 && 'justify-end',
                  )}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanSort() && <SortIcon />}
                </span>
              </th>
            ))}
            <th scope="col" className="typo-secondary text-right text-secondary-text px-2 pr-4 pb-2">
              <div className="w-10 h-1" />
            </th>
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id} data-testid={`product-table-row-${row.original.id}`}>
            {row.getVisibleCells().map((cell, index) => (
              <td
                key={cell.id}
                title={cell.getValue() as string}
                className={cn(
                  'typo-secondary text-left border border-l-0 border-r-0 border-other-stroke mb-2 p-2',
                  index === 0 && 'border-l pl-4 min-w-[150px] rounded-tl rounded-bl',
                  index === 1 && 'min-w-[200px] rounded-tl-none rounded-bl-none',
                  // we don't apply truncate to the updatedAt row
                  index !== row.getVisibleCells().length - 1 && 'truncate whitespace-nowrap overflow-hidden max-w-0',
                  index === row.getVisibleCells().length - 1 && 'text-right',
                )}
              >
                {index === 0 && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      onRowClick && onRowClick(row.original.id);
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Button>
                )}
                {index !== 0 && flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
            <td className="typo-secondary text-right border border-l-0 border-other-stroke pr-4 align-center pt-1 rounded-tr rounded-br">
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger
                  asChild
                  data-testid={`product-table-row-${row.original.id}-dropdown-trigger`}
                  className="focus:outline-primary"
                >
                  <Button variant="icon">
                    <DotsIcon className="cursor-pointer" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    data-testid={`product-table-row-${row.original.id}-dropdown-trigger-edit`}
                    disabled={!isProductEditEnabled(guid, row.original)}
                    onClick={() => {
                      if (row.original.lifecycleStatus === LifecycleStatus.Retired) return;
                      handleEdit(row.original as Product);
                    }}
                  >
                    <T {...productTableMessages.actionsEdit} />
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid={`product-table-row-${row.original.id}-dropdown-trigger-use-as-new-template`}
                    disabled={!isProductCopyEnabled(row.original)}
                    onClick={() => {
                      if (row.original.lifecycleStatus === LifecycleStatus.Retired) return;
                      handleUseAsNewTemplate(row.original as Product);
                    }}
                  >
                    <T {...productTableMessages.actionsUseAsNewTemplate} />
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid={`product-table-row-${row.original.id}-dropdown-trigger-status-change`}
                    disabled={!isProductStatusChangeEnabled(guid, row.original)}
                    onClick={() => handleStatusChange(row.original)}
                    className={cn(row.original.lifecycleStatus === LifecycleStatus.InProgress && 'text-system-error')}
                  >
                    {row.original.lifecycleStatus !== LifecycleStatus.Retired && (
                      <T {...productTableMessages.actionsSetAsRetired} />
                    )}
                    {row.original.lifecycleStatus === LifecycleStatus.Retired && (
                      <T {...productTableMessages.actionsSetAsActive} />
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-system-error"
                    disabled={!isProductDeleteEnabled(guid, row.original)}
                    data-testid={`product-table-row-${row.original.id}-dropdown-trigger-delete`}
                    onClick={() => {
                      handleDelete(row.original as Product);
                    }}
                  >
                    <T {...productTableMessages.actionsDelete} />
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
